import { useSyncExternalStore, type MouseEvent, type ReactElement, type ReactNode } from "react";

/**
 * The staff app's one list of toasts. Both toast APIs write here:
 * - `toast({ title, description, variant, important, duration })` (src/hooks/use-toast.ts)
 * - `toast(title, { id, description, duration, action })`, `toast.success()`, `toast.error()`
 *   (src/components/ui/sonner.ts, the sonner-style API)
 * and one viewport renders it (src/components/ui/toaster.tsx).
 *
 * Owner (2026-10-30): every toast closes 5 s after it shows, with a visible
 * countdown; the few that need action (a KOT that didn't print, a QR order
 * that was auto-accepted, a guest calling a waiter, ...) stay until closed.
 * Those are the ones their caller shows with `duration: Infinity`.
 */

/** How long every toast that is not sticky stays. Any other duration a caller passes is ignored. */
export const TOAST_DURATION_MS = 5_000;
/** At most this many `important` toasts (toast({ important: true })) at once; a newer one closes the oldest. */
export const IMPORTANT_TOAST_LIMIT = 3;
/** A closed toast stays in the list this long, for its exit animation. */
export const TOAST_EXIT_MS = 200;

export type ToastTone = "default" | "destructive" | "success" | "error";
/** Which API showed it: each API's dismiss-all closes only its own toasts, as before. */
export type ToastSource = "toast" | "sonner";

/** A sonner-style action: a label and what it does. Clicking it also closes the toast. */
export interface ToastButton {
  label: ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

export interface ToastInput {
  /** Showing a toast under an id that is still up replaces it in place (and its countdown starts over). */
  id?: string;
  source: ToastSource;
  title?: ReactNode;
  description?: ReactNode;
  /** A <ToastAction> element (toast()) or a { label, onClick } (sonner-style). */
  action?: ReactElement | ToastButton;
  tone?: ToastTone;
  /** `Infinity`: stays until closed. Any other value is ignored: the toast closes after TOAST_DURATION_MS. */
  duration?: number;
  important?: boolean;
  /** The person closed it (X or Escape). Not called for an action click or a programmatic dismiss. */
  onDismiss?: () => void;
  /** Its countdown ran out. */
  onAutoClose?: () => void;
}

export interface ToastItem {
  id: string;
  source: ToastSource;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactElement | ToastButton;
  tone: ToastTone;
  /** Stays until closed: no countdown. */
  sticky: boolean;
  important: boolean;
  /** False while it plays its exit animation. */
  open: boolean;
  /** Bumps each time it is shown again under its id or repeated: its countdown starts over. */
  version: number;
  /** Countdown ms left as of `since` (Infinity when sticky). */
  remaining: number;
  /** Date.now() when the countdown last started or resumed; null while paused (or sticky, or closed). */
  since: number | null;
  onDismiss?: () => void;
  onAutoClose?: () => void;
}

/** Why a toast closed: only "user" and "auto" call back (as sonner did). */
export type ToastCloseReason = "user" | "auto" | "action" | "api";

let toasts: readonly ToastItem[] = [];
const listeners = new Set<() => void>();
const countdowns = new Map<string, ReturnType<typeof setTimeout>>();
const removals = new Map<string, ReturnType<typeof setTimeout>>();
/** Why the countdowns are paused (hover, focus, a hidden tab); empty while they run. */
const pauses = new Set<string>();
let count = 0;

function emit(next: readonly ToastItem[]) {
  toasts = next;
  for (const listener of listeners) listener();
}

function clearCountdown(id: string) {
  const timer = countdowns.get(id);
  if (timer !== undefined) clearTimeout(timer);
  countdowns.delete(id);
}

function cancelRemoval(id: string) {
  const timer = removals.get(id);
  if (timer !== undefined) clearTimeout(timer);
  removals.delete(id);
}

/** (Re)arms a running countdown; a paused, sticky or closed toast has none. */
function arm(toast: ToastItem) {
  clearCountdown(toast.id);
  if (toast.sticky || !toast.open || toast.since === null) return;
  countdowns.set(
    toast.id,
    setTimeout(() => {
      countdowns.delete(toast.id);
      closeToast(toast.id, "auto");
    }, toast.remaining),
  );
}

/** Milliseconds before the toast closes by itself (Infinity when sticky). */
export function toastRemainingMs(toast: ToastItem, now = Date.now()): number {
  if (toast.sticky) return Infinity;
  return toast.since === null ? toast.remaining : Math.max(0, toast.remaining - (now - toast.since));
}

function build(input: ToastInput, id: string, version: number): ToastItem {
  const sticky = input.duration === Infinity;
  return {
    id,
    source: input.source,
    title: input.title,
    description: input.description,
    action: input.action,
    tone: input.tone ?? "default",
    sticky,
    important: !!input.important,
    open: true,
    version,
    remaining: sticky ? Infinity : TOAST_DURATION_MS,
    since: sticky || pauses.size > 0 ? null : Date.now(),
    onDismiss: input.onDismiss,
    onAutoClose: input.onAutoClose,
  };
}

const sameText = (a: ReactNode, b: ReactNode) => (a === undefined || typeof a === "string") && a === b;

/** The same plain message is already up (two quick saves): it shows once, its countdown starting over. */
function repeatOf(input: ToastInput): ToastItem | undefined {
  if (input.action || input.duration === Infinity) return undefined;
  const tone = input.tone ?? "default";
  return toasts.find(
    (t) =>
      t.open &&
      !t.sticky &&
      !t.action &&
      t.source === input.source &&
      t.tone === tone &&
      sameText(t.title, input.title) &&
      sameText(t.description, input.description),
  );
}

/** Shows a toast and returns its id. */
export function showToast(input: ToastInput): string {
  const existing = input.id !== undefined ? toasts.find((t) => t.id === input.id) : repeatOf(input);
  let shown: ToastItem;
  if (existing) {
    cancelRemoval(existing.id);
    shown = build(input, existing.id, existing.version + 1);
    // Under its id it stays where it is (as sonner did); a repeated message moves to the newest place.
    emit(
      input.id !== undefined
        ? toasts.map((t) => (t.id === existing.id ? shown : t))
        : [...toasts.filter((t) => t.id !== existing.id), shown],
    );
  } else {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    shown = build(input, input.id ?? `toast-${count}`, 0);
    emit([...toasts, shown]);
  }
  arm(shown);
  if (shown.important) {
    const important = toasts.filter((t) => t.open && t.important && t.source === shown.source);
    for (const old of important.slice(0, Math.max(0, important.length - IMPORTANT_TOAST_LIMIT))) closeToast(old.id, "api");
  }
  return shown.id;
}

/** Changes a toast that is still up, keeping its countdown. */
export function updateToast(id: string, patch: Partial<Pick<ToastItem, "title" | "description" | "action" | "tone">>) {
  if (!toasts.some((t) => t.id === id && t.open)) return;
  emit(toasts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
}

/** Closes a toast; it leaves the list after its exit animation. */
export function closeToast(id: string, reason: ToastCloseReason = "api") {
  const toast = toasts.find((t) => t.id === id);
  if (!toast || !toast.open) return;
  clearCountdown(id);
  emit(toasts.map((t) => (t.id === id ? { ...t, open: false, remaining: toastRemainingMs(t), since: null } : t)));
  cancelRemoval(id);
  removals.set(
    id,
    setTimeout(() => {
      removals.delete(id);
      emit(toasts.filter((t) => t.id !== id));
    }, TOAST_EXIT_MS),
  );
  if (reason === "user") toast.onDismiss?.();
  if (reason === "auto") toast.onAutoClose?.();
}

/** Closes every open toast one API showed (no callbacks). */
export function closeAllToasts(source: ToastSource) {
  for (const t of toasts) if (t.open && t.source === source) closeToast(t.id, "api");
}

/** Holds every countdown (the pointer is over the toasts, one has focus, the tab is hidden). */
export function pauseToasts(reason: string) {
  if (pauses.has(reason)) return;
  pauses.add(reason);
  if (pauses.size > 1) return;
  const now = Date.now();
  emit(
    toasts.map((t) => {
      if (t.since === null) return t;
      clearCountdown(t.id);
      return { ...t, remaining: toastRemainingMs(t, now), since: null };
    }),
  );
}

/** Lets the countdowns run again once nothing else holds them. */
export function resumeToasts(reason: string) {
  if (!pauses.delete(reason) || pauses.size > 0) return;
  const now = Date.now();
  emit(toasts.map((t) => (t.open && !t.sticky && t.since === null ? { ...t, since: now } : t)));
  for (const t of toasts) arm(t);
}

export function toastsPaused(): boolean {
  return pauses.size > 0;
}

export function getToasts(): readonly ToastItem[] {
  return toasts;
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Every toast, oldest first (closing ones included, `open: false`). */
export function useToastList(): readonly ToastItem[] {
  return useSyncExternalStore(subscribeToasts, getToasts, getToasts);
}

/** Test hook: no toasts, no timers, nothing paused. */
export function resetToastsForTests() {
  for (const timer of countdowns.values()) clearTimeout(timer);
  for (const timer of removals.values()) clearTimeout(timer);
  countdowns.clear();
  removals.clear();
  pauses.clear();
  emit([]);
}
